import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Navbar from "wbr/components/navbar/Navbar";
import Footer from "wbr/components/footer/Footer";
import Sidebar from "wbr/components/sidebar/Sidebar";
import MainContent from "wbr/components/MainContent";
import { useUser } from "shared/context/UserContext";
import { usePermissions } from "shared/context/PermissionsContext";
import { useFunctionConfig } from "./useFunctionConfig";
import { useReportStatus } from "./useReportStatus";
import { useWeekYear, CURRENT_WBR_WEEK, CURRENT_YEAR as CY } from "./useWeekYear";
import { usePrefetchReports } from "./usePrefetchReports";
import "./FunctionPage.css";

const INITIAL_WEEK = CURRENT_WBR_WEEK;
const INITIAL_YEAR = CY;

// ─── Helpers ───
function findSidebarItem(sidebarOptions, key) {
  if (!sidebarOptions || !key) return null;
  for (const section of Object.values(sidebarOptions)) {
    const found = section.items.find((item) => item.key === key);
    if (found) return found;
  }
  return null;
}

function getFirstSidebarKey(sidebarOptions) {
  if (!sidebarOptions) return null;
  const firstSection = Object.values(sidebarOptions)[0];
  return firstSection?.items?.[0]?.key ?? null;
}

const ADMIN_VIEW_KEY = "__admin__";
const UPLOAD_VIEW_KEY = "__upload__";

function FunctionPage() {
  const { id } = useParams();
  const user = useUser();

  // ─── Permissions ───
  const {
    fetchPermissions,
    loading: permLoading,
    fetched: permFetched,
    hasAccess,
    canAdmin,
    canUpload,
    canEdit,
    role,
  } = usePermissions();

  // ─── UI state ───
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [theme, setTheme] = useState("light");
  const [activeSidebarKey, setActiveSidebarKey] = useState(null);

  // ─── Config first: we only need its start_year to shape the year dropdown ───
  // selectedWeek/selectedYear come from useWeekYear below, but useWeekYear's
  // SELECTION state is seeded from "today" and is NOT affected by start_year —
  // start_year only builds the yearList. So there is no circular dependency:
  // config's start_year flows into useWeekYear across renders, and useWeekYear's
  // selection flows into the config fetch.
  const [selectedWeek, setSelectedWeek] = useState(INITIAL_WEEK);
  const [selectedYear, setSelectedYear] = useState(INITIAL_YEAR);

  const { config: functionConfig, loading, error, defaultSidebarKey } =
    useFunctionConfig(id, selectedWeek, selectedYear);

  // ─── Week/Year management (single source of truth) ───
  const {
    weekList, yearList, CURRENT_YEAR,
  } = useWeekYear(
    functionConfig?.function_details?.start_year,
    { selectedWeek, selectedYear, setSelectedWeek, setSelectedYear }
  );

  // ─── Report Status ───
  const { reportStatus, reportStatusLoading, refreshReportStatus } =
    useReportStatus(id, selectedYear, selectedWeek, functionConfig?.function_sidebar);

  // ─── Layer 2: prefetch report callouts into cache for instant report opens ───
  usePrefetchReports(id, selectedYear, selectedWeek, functionConfig?.function_sidebar);

  // ─── Fetch permissions ───
  useEffect(() => {
    if (user?.userId && id) {
      fetchPermissions(user.userId, id, CURRENT_YEAR);
    }
  }, [user?.userId, id, fetchPermissions, CURRENT_YEAR]);

  // ─── Set default sidebar key when config loads ───
  useEffect(() => {
    if (defaultSidebarKey) setActiveSidebarKey(defaultSidebarKey);
  }, [defaultSidebarKey]);

  // ─── View mode ───
  const viewMode = (() => {
    if (activeSidebarKey === ADMIN_VIEW_KEY) return "admin";
    if (activeSidebarKey === UPLOAD_VIEW_KEY) return "upload";
    return "content";
  })();

  const toggleTheme = () => setTheme((prev) => (prev === "light" ? "dark" : "light"));

  const handleAdminClick = () => { if (canAdmin) setActiveSidebarKey(ADMIN_VIEW_KEY); };
  const handleUploadClick = () => { if (canUpload) setActiveSidebarKey(UPLOAD_VIEW_KEY); };
  const handleSidebarItemClick = (key) => setActiveSidebarKey(key);

  // ─── Loading ───
  if (loading || permLoading || !permFetched) {
    return (
      <div className="function-page-loading">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (error) return <div className="function-page-error">Error: {error}</div>;

  // ─── Access denied ───
  if (permFetched && !hasAccess) {
    return (
      <div className={`function-page ${theme}`}>
        <Navbar
          theme={theme}
          toggleTheme={toggleTheme}
          function_name={functionConfig?.function_details?.label}
          userLogin={user.userId}
          weekList={[]}
          yearList={[]}
          defaultWeek={selectedWeek}
          defaultYear={selectedYear}
          showAdminButton={false}
          showUploadButton={false}
        />
        <div className="access-denied">
          <div className="access-denied__card">
            <div className="access-denied__icon">🔒</div>
            <h2>Access Denied</h2>
            <p>You don't have permission to view this function.<br />Contact the function admin to request access.</p>
            <span className="access-denied__user">Logged in as: <strong>{user.userId}</strong></span>
          </div>
        </div>
        <Footer theme={theme} />
      </div>
    );
  }

  // ─── Permission guards ───
  const effectiveViewMode = (() => {
    if (viewMode === "admin" && !canAdmin) return "content";
    if (viewMode === "upload" && !canUpload) return "content";
    return viewMode;
  })();

  const effectiveActiveSidebarKey = (() => {
    if (viewMode === "admin" && !canAdmin) return functionConfig?.sidebar_check || getFirstSidebarKey(functionConfig?.function_sidebar);
    if (viewMode === "upload" && !canUpload) return functionConfig?.sidebar_check || getFirstSidebarKey(functionConfig?.function_sidebar);
    return activeSidebarKey;
  })();

  const effectiveActiveItem = effectiveViewMode !== "content"
    ? null
    : findSidebarItem(functionConfig?.function_sidebar, effectiveActiveSidebarKey);

  return (
    <div className={`function-page ${theme}`}>
      <Navbar
        theme={theme}
        toggleTheme={toggleTheme}
        sidebarOpen={sidebarOpen}
        toggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        function_name={functionConfig?.function_details?.label}
        userLogin={user.userId}
        userRole={role}
        weekList={weekList}
        yearList={yearList}
        defaultWeek={selectedWeek}
        defaultYear={selectedYear}
        onWeekChange={setSelectedWeek}
        onYearChange={setSelectedYear}
        onAdminClick={handleAdminClick}
        onUploadClick={handleUploadClick}
        isAdminActive={viewMode === "admin"}
        isUploadActive={viewMode === "upload"}
        showAdminButton={canAdmin}
        showUploadButton={canUpload}
      />

      <div className="function-page__body">
        <Sidebar
          isOpen={sidebarOpen}
          theme={theme}
          sidebar_options={functionConfig?.function_sidebar}
          activeSidebarKey={effectiveViewMode === "content" ? effectiveActiveSidebarKey : null}
          onItemClick={handleSidebarItemClick}
          reportStatus={reportStatus}
          reportStatusLoading={reportStatusLoading}
        />
        <main className={`function-page__main ${sidebarOpen ? "function-page__main--sidebar-open" : ""}`}>
          <MainContent
            theme={theme}
            function_id={id}
            userLogin={user.userId}
            week={selectedWeek}
            year={selectedYear}
            activeItem={effectiveActiveItem}
            functionConfig={functionConfig}
            viewMode={effectiveViewMode}
            userRole={role}
            canEdit={canEdit}
            onUploadSuccess={refreshReportStatus}
          />
        </main>
      </div>

      <Footer theme={theme} />
    </div>
  );
}

export default FunctionPage;
